import YasharalLiveFeatureSections from "./YasharalLiveFeatureSections";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_FREE_PLAN = null;
function PlanCard({ plan }) {
  const standardPrice = plan.monthly_price_usd;
  const foundingAvailable = plan.launch_offer_available !== false && plan.launch_offer_active;
  const launchPrice = foundingAvailable ? plan.launch_price_usd : null;
  const foundingAnnual = foundingAvailable ? plan.launch_annual_price_usd : null;
  const price = standardPrice == null
    ? "Custom"
    : standardPrice === 0
      ? "Free"
      : launchPrice != null
        ? `${launchPrice}/mo`
        : `${standardPrice}/mo`;

  return (
    <article className="min-w-0 self-start rounded-2xl border border-slate-700 bg-[#111318] p-4 sm:p-5">
      <div>
        <h3 className="break-words text-lg font-semibold text-white">{plan.name}</h3>
        <div className="mt-1 text-2xl font-bold text-[#9dc1ff]">{price}</div>
        {launchPrice != null && (
          <div className="mt-2 rounded-lg border border-amber-400/20 bg-amber-400/5 p-2.5 text-xs leading-relaxed">
            <div className="font-semibold text-amber-200">Founding monthly offer</div>
            <div className="text-slate-300">${launchPrice}/mo for your first {plan.launch_price_months} billing cycles, then <span className="font-semibold">${standardPrice}/mo</span>.</div>
          </div>
        )}
        {standardPrice > 0 && plan.annual_price_usd != null && (
          <div className="mt-2 rounded-lg border border-slate-700 bg-black/10 p-2.5 text-xs leading-relaxed text-slate-300">
            <div><span className="font-semibold">Normal monthly:</span> ${Number(standardPrice).toFixed(2)}/mo</div>
            <div><span className="font-semibold">Normal annual:</span> ${Number(plan.annual_price_usd).toFixed(2)}/year · {plan.annual_discount_percent}% below 12 normal monthly payments</div>
            {foundingAnnual != null && (
              <div className="mt-1 text-amber-200"><span className="font-semibold">Founding annual:</span> ${Number(foundingAnnual).toFixed(2)} for year one · {plan.launch_annual_discount_percent}% off the normal annual price · {plan.launch_total_savings_vs_monthly_percent}% below 12 normal monthly payments</div>
            )}
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-300">
          {plan.max_meeting_participants != null && <span className="rounded-full bg-white/5 px-2.5 py-1">{plan.max_meeting_participants} meeting participants</span>}
          {plan.max_presenters != null && <span className="rounded-full bg-white/5 px-2.5 py-1">{plan.max_presenters} Studio presenters</span>}
          {plan.resolution && <span className="rounded-full bg-white/5 px-2.5 py-1">{plan.resolution}</span>}
          {plan.max_destinations != null && <span className="rounded-full bg-white/5 px-2.5 py-1">{plan.max_destinations} live destination{plan.max_destinations === 1 ? "" : "s"}</span>}
        </div>
      </div>

      {!!(plan.tier_highlights || []).length && (
        <div className="mt-4 rounded-xl border border-[#8bb6ff]/30 bg-[#183c73]/15 p-3">
          <div className="text-xs font-bold uppercase tracking-[0.14em] text-[#9dc1ff]">New at this tier</div>
          <ul className="mt-2 space-y-2">
            {plan.tier_highlights.map((feature) => (
              <li key={feature} className="flex gap-2 text-sm leading-relaxed text-white">
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#9dc1ff]" />
                <span className="min-w-0 break-words">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <YasharalLiveFeatureSections sections={plan.feature_sections || {}} />
    </article>
  );
}

export default function YasharalLivePublicFeatures() {
  const [freePlan, setFreePlan] = useState(FALLBACK_FREE_PLAN);
  const [paidPlans, setPaidPlans] = useState([]);
  const [customPlan, setCustomPlan] = useState(null);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/communications/billing/plans`)
      .then((response) => {
        if (!active) return;
        setFreePlan(response.data?.free || null);
        setPaidPlans(response.data?.plans || []);
        setCustomPlan(response.data?.custom || null);
      })
      .catch(() => {
        if (active) {
          setFreePlan(null);
          setPaidPlans([]);
          setCustomPlan(null);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const plans = useMemo(
    () => [...(freePlan ? [freePlan] : []), ...paidPlans, ...(customPlan ? [customPlan] : [])],
    [freePlan, paidPlans, customPlan]
  );

  return (
    <section id="features" className="min-w-0 scroll-mt-6">
      <div className="text-center">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#9dc1ff]">Plans & features</p>
        <h2 className="mt-2 text-2xl font-semibold text-white sm:text-3xl">See what Yashar’al Live includes before you sign in</h2>
        <p className="mx-auto mt-3 max-w-3xl text-sm text-slate-400 sm:text-base">
          Yashar’al Live combines browser-based production, multistreaming, interactive meetings, recordings, collaboration, AI-assisted workflows, webinars and team administration in one service.
        </p>
        {paidPlans.some(plan => plan.launch_offer_active) && (() => {
          const offer = paidPlans.find(plan => plan.launch_offer_active) || {};
          return (
            <>
              <p className="mx-auto mt-2 max-w-3xl text-xs text-amber-300/90">
                Founding launch offer: subscribe during the first {offer.launch_promotion_window_days} days after official launch. Monthly subscribers receive the special rate for their first {offer.launch_price_months} billing cycles even when those cycles extend beyond the signup window. Founding annual subscribers receive {offer.launch_annual_discount_percent}% off the normal annual price for year one, then renew at the normal annual rate.
              </p>
              {!offer.launch_promotion_start_at && (
                <p className="mx-auto mt-1 max-w-3xl text-[11px] text-slate-500">
                  Official launch date has not been established yet; the promotional cutoff begins from that date once it is set.
                </p>
              )}
            </>
          );
        })()}
        <p className="mx-auto mt-2 max-w-3xl text-xs text-slate-500">
          * Coming soon — shown now so the full tier roadmap is clear. Features without an asterisk are intended to be available in the current launch build.
        </p>
      </div>

      <div className="mt-6 grid min-w-0 grid-cols-1 items-start gap-4 md:grid-cols-2 2xl:grid-cols-3">
        {plans.map((plan) => (
          <PlanCard key={plan.key || plan.name} plan={plan} />
        ))}
      </div>
    </section>
  );
}
